import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import DateRangeFilter from '../components/DateRangeFilter';
import { useLanguage } from '../context/LanguageContext';

// Pantalla de CONSULTA, solo lectura. Emitir los documentos —que recalcula la semana— vive en
// Generar Reportes: una pantalla llamada "Historial" no deberia reescribir lo que muestra.
export default function Records() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [facility, setFacility] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/timecards', { params: { search, dateFrom, dateTo, facility: facility || undefined, page, limit } });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('rec.fetchError') || 'Error loading records.' });
    }
    setLoading(false);
  }, [search, dateFrom, dateTo, facility, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Año a dos dígitos: la columna va en una sola linea (ver el comentario de la columna Periodo) y
  // "2/23/2026 - 3/1/2026" gastaba ~40px de mas en algo que se lee igual como "2/23/26 - 3/1/26".
  const formatDate = (d) => {
    if (!d) return '';
    const s = typeof d === 'string' ? d.substring(0, 10) : d;
    const dt = new Date(s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : ''));
    return isNaN(dt.getTime())
      ? ''
      : dt.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
  };

  // Con segundos, igual que Generar Reportes y las exportaciones: a minutos, las columnas de dias
  // no sumaban a la vista el Total de la fila (08:01 + 07:59 se lee como 16:00 y el total decia
  // otra cosa). Salen del valor guardado, que el calculo redondea al segundo (ver roundHrs).
  const fmtHrs = (val) => {
    if (!val || val === 0) return '-';
    const totalSec = Math.round(val * 3600);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Tooltip explaining the clock-in the day was PAID with — the same time Reports used to
  // calculate it and the exports print. Comes from the timecard itself (day_info.in), so it
  // cannot drift from the decision taken in the calculation modal.
  const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  const inTooltip = (r, dayKey) => {
    const info = r.day_info?.[dayKey];
    if (!info) return '';
    if (!info.in) {
      // A worked week with no In on any day = timecard calculated before the In was stored; the
      // hint invites recalculating it. A single day without an In is normal (holiday, PTO, or a
      // day with no punches), so it gets no tooltip.
      const noneStored = !DAY_KEYS.some(d => r.day_info?.[d]?.in);
      return noneStored && r.total_hrs > 0 && r[`${dayKey}_hrs`] > 0 ? t('rec.inNotStored') : '';
    }
    const parts = [`${t('rec.paidIn')}: ${info.in}`];
    if (info.shiftStart) parts.push(`${t('rec.shiftStartShort')}: ${info.shiftStart}`);
    if (info.punch && info.punch !== info.in) parts.push(`${t('rec.firstPunch')}: ${info.punch}`);
    if (info.reason) parts.push(t(`rec.inWhy.${info.reason}`));
    if (info.lateMin > 0) parts.push(`${info.lateMin} min ${t('rec.lateBy')}`);

    // The exit side, only for weeks calculated since the Out is stored. On an older one there is
    // nothing to say about it, and inventing a "missing clock-out" on every historical day would be
    // worse than saying nothing.
    if (r.has_stored_outs) {
      if (info.out) {
        parts.push(`${t('rec.paidOut')}: ${info.out}`);
        if (info.punchOut && info.punchOut !== info.out) parts.push(`${t('rec.punchOut')}: ${info.punchOut}`);
      } else {
        parts.push(t('rec.noOut'));
      }
      if (info.lunch !== null && info.lunch !== undefined) {
        parts.push(`${info.lunch ? t('rec.lunchOn') : t('rec.lunchOff')}${info.lunchEdited ? ` (${t('rec.lunchForced')})` : ''}`);
      }
    } else if (r[`${dayKey}_hrs`] > 0) {
      parts.push(t('rec.outNotStored'));
    }
    return parts.join(' · ');
  };

  // Reasons that come from a DECISION somebody took: a time typed in the calculation modal, an
  // authorization with a captured time, or — on weeks calculated before the automatic rounding was
  // removed — a forgiven retardo. Everything else is the day being paid with the punch the employee
  // actually made, which is now the default and nobody picked.
  const DECIDED_REASONS = new Set(['edited', 'auth', 'forgiven']);

  // Second line of every day cell: the span the day was PAID with, visible without hovering.
  // Highlighted ONLY on the days a decision was applied, so marking 4 days in the modal highlights
  // 4 days here — not every day the calculation happened to adjust on its own.
  // On a week calculated before the Out was stored only the In shows, exactly as before.
  const paidSpanLine = (r, dayKey) => {
    const info = r.day_info?.[dayKey];
    if (!info?.in) return null;
    const decided = DECIDED_REASONS.has(info.reason);
    const showOut = r.has_stored_outs;
    return (
      <div style={{
        fontSize: 9.5,
        lineHeight: 1.2,
        marginTop: 1,
        fontWeight: decided ? 700 : 400,
        color: decided ? 'var(--success)' : 'var(--text-muted)',
      }}>
        {info.in}{decided ? ' •' : ''}
        {showOut && (
          // "—" is a day with a clock-in and no exit at all: the hours are the provisional "by law"
          // ones, and that is worth seeing without hovering.
          <>
            <br />
            <span style={{ color: info.out ? undefined : 'var(--warning)' }}>
              {info.out || '—'}
            </span>
          </>
        )}
      </div>
    );
  };

  // Third line, only on the days whose lunch was decided BY HAND. The automatic rule is the norm
  // and stays quiet; the exception is what deserves to be visible.
  const lunchLine = (r, dayKey) => {
    const info = r.day_info?.[dayKey];
    if (!info?.lunchEdited || info.lunch === null || info.lunch === undefined) return null;
    return (
      <div style={{ fontSize: 9, lineHeight: 1.15, fontWeight: 700, color: 'var(--success)' }}>
        {info.lunch ? '−00:30' : t('rec.lunchNone')}
      </div>
    );
  };

  const dayCell = (r, dayKey) => {
    const value = r[`${dayKey}_hrs`];
    const isIncomplete = r.incomplete_days?.[dayKey];
    const lateMin = r.late_days?.[dayKey] || 0;
    const inTip = inTooltip(r, dayKey);
    // The whole cell carries the explanation (paid In, real punch, why it changed).
    const wrap = (hoursNode, extraTip) => (
      <div title={[extraTip, inTip].filter(Boolean).join('\n')} style={{ whiteSpace: 'nowrap' }}>
        {hoursNode}
        {paidSpanLine(r, dayKey)}
        {lunchLine(r, dayKey)}
      </div>
    );

    // Missing clock-out (red) takes priority over late arrival (yellow).
    if (isIncomplete) {
      return wrap(
        <span style={{
          display: 'inline-block',
          padding: '1px 4px',
          borderRadius: 4,
          background: 'rgba(231,76,60,0.18)',
          color: 'var(--danger)',
          fontWeight: 700,
        }}>
          {fmtHrs(value)} ⚠
        </span>,
        t('rec.missingOutTooltip')
      );
    }
    if (lateMin > 0) {
      // 1h–2h late → yellow (per meeting). More than 2h late → stronger orange
      // to flag for priority review, without clashing with the red used for
      // missing clock-out.
      const veryLate = lateMin > 120;
      return wrap(
        <span style={{
          display: 'inline-block',
          padding: '1px 4px',
          borderRadius: 4,
          background: veryLate ? 'rgba(230,126,34,0.22)' : 'rgba(245,158,11,0.18)',
          color: veryLate ? '#c0560f' : 'var(--warning)',
          fontWeight: veryLate ? 700 : 600,
        }}>
          {fmtHrs(value)}{veryLate ? ' ⚠' : ''}
        </span>,
        `${t('ts.lateTooltip') || 'Late arrival'} (${lateMin} min)`
      );
    }
    return wrap(<span>{fmtHrs(value)}</span>, '');
  };

  const columns = [
    { header: t('common.badge'), accessor: 'badgenum_tc' },
    { header: t('hrs.facility'), render: (r) => r.facility_tc ? <span className="badge badge-info">{r.facility_tc}</span> : '-' },
    {
      header: t('hrs.employee'),
      // Acotada y en dos lineas si hace falta: un nombre largo se llevaba solo ~160px de ancho, y
      // aqui el ancho es lo que escasea. El nombre completo sigue visible en el tooltip.
      style: { maxWidth: 118 },
      render: (r) => {
        const full = r.name_emp ? `${r.name_emp} ${r.last_name_emp}` : '-';
        return <span title={full} style={{ display: 'inline-block', lineHeight: 1.25 }}>{full}</span>;
      },
    },
    {
      header: t('rec.period'),
      // One line: with 17 columns the browser was breaking "2/23/2026 - 3/1/2026" in two.
      render: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>{`${formatDate(r.start_tc)} - ${formatDate(r.end_tc)}`}</span>
      ),
    },
    { header: t('day.mon'), render: (r) => dayCell(r, 'mon') },
    { header: t('day.tue'), render: (r) => dayCell(r, 'tue') },
    { header: t('day.wed'), render: (r) => dayCell(r, 'wed') },
    { header: t('day.thu'), render: (r) => dayCell(r, 'thu') },
    { header: t('day.fri'), render: (r) => dayCell(r, 'fri') },
    { header: t('day.sat'), render: (r) => dayCell(r, 'sat') },
    { header: t('day.sun'), render: (r) => dayCell(r, 'sun') },
    { header: t('hrs.total'), render: (r) => <strong style={{ color: 'var(--accent)' }}>{fmtHrs(r.total_hrs)}</strong> },
    { header: t('hrs.regular'), render: (r) => fmtHrs(r.regular_hrs) },
    { header: t('hrs.overtime'), render: (r) => <span style={{ color: r.overtime_hrs > 0 ? 'var(--warning)' : 'inherit' }}>{fmtHrs(r.overtime_hrs)}</span> },
    { header: t('hrs.paidAbs'), render: (r) => <span style={{ color: r.paid_abs_hrs > 0 ? '#D35400' : 'inherit' }}>{fmtHrs(r.paid_abs_hrs)}</span> },
    {
      header: t('rec.lateDays') || 'Late days',
      render: (r) => {
        const count = r.late_count || 0;
        if (count === 0) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
        return (
          <span
            title={t('rec.lateDaysTooltip') || 'Days with late arrival (>= 60 min)'}
            style={{
              display: 'inline-block',
              minWidth: 18,
              padding: '1px 6px',
              borderRadius: 12,
              background: 'var(--warning)',
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
              fontSize: 11,
            }}
          >
            {count}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('rec.title')}</h1>
      </div>

      <div className="filters-bar">
        <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('rec.search')} />
        <select className="form-input" style={{ width: 160, padding: '8px 10px', height: 38 }}
          value={facility} onChange={e => { setFacility(e.target.value); setPage(1); }}>
          <option value="">{t('export.selectFacility')}</option>
          <option value="MIA">MIA</option>
          <option value="AUSWH">AUSWH</option>
        </select>
        <DateRangeFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={(v) => { setDateFrom(v); setPage(1); }}
          onDateToChange={(v) => { setDateTo(v); setPage(1); }}
          onRefresh={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
        />
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px 2px' }}>
        {t('rec.inLegend')}
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={data} loading={loading} emptyMessage={t('rec.empty')} className="table-compact" />
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
      </div>

    </div>
  );
}
